import {
	DataGridPremium,
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-premium';
import { useMutation } from '@parkchirp/api-client';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import { FC, useCallback, useState } from 'react';

import { UPDATE_PARKER_SEQUENCE_NUMBER } from '../../../mutations';
import { FutureParker, SetShowSnackbar } from '../../../types';
import { datagridFilterPanelAttributes, dayjs } from '../../../utils';
import { DatagridLinearProgress } from '../../loading';
import { FutureParkerEditor } from './futureParkerEditor';

type FutureParkersTableProps = {
	parkers: FutureParker[];
	loading: boolean;
	setShowSnackbar: SetShowSnackbar;
	handleWaitForResequencing: () => Promise<void>;
};

const dateOperators = getGridDateOperators();
const numericOperators = getGridNumericOperators();

export const FutureParkersTable: FC<FutureParkersTableProps> = ({
	parkers,
	loading,
	setShowSnackbar,
	handleWaitForResequencing,
}) => {
	const [{ page, pageSize }, setPaginationModel] = useState<GridPaginationModel>({
		pageSize: 25,
		page: 0,
	});

	const [filterModel, setFilterModel] = useState<GridFilterModel>({
		items: [],
	});

	const [updateParkerSequenceNumber, { loading: sequenceNumberUpdating }] =
		useMutation(UPDATE_PARKER_SEQUENCE_NUMBER);

	const handleSequenceNumberUpdate = useCallback(
		async (parker: FutureParker, sequenceNumber: number) => {
			const validateSequenceNumber = (value: number) => {
				if (!Number.isInteger(value)) {
					return 'Sequence Number must be a whole number';
				}

				return null;
			};

			const validationError = validateSequenceNumber(sequenceNumber);

			if (validationError) {
				setShowSnackbar({
					message: validationError,
					severity: 'error',
					autoHideDuration: 5000,
				});

				return;
			}

			try {
				await updateParkerSequenceNumber({
					variables: {
						sequenceNumber,
						parkerId: parker.id,
						sequenceNumberLocked: parker.sequenceNumberLocked,
					},
					context: { apiVersion: 'v2' },
					onCompleted: async () => {
						setShowSnackbar({
							message: 'Sequence number updated',
							severity: 'success',
							autoHideDuration: 3000,
						});

						await handleWaitForResequencing();
					},
				});
			} catch {
				setShowSnackbar({
					message: 'Failed to update sequence number',
					severity: 'error',
					autoHideDuration: 5000,
				});
			}
		},
		[setShowSnackbar, updateParkerSequenceNumber, handleWaitForResequencing],
	);

	const columns: GridColDef<FutureParker>[] = [
		{
			field: 'sequenceNumber',
			headerName: 'Sequence Number',
			editable: false,
			groupable: false,
			flex: 2,
			filterOperators: numericOperators,
			renderCell: ({ row: parker }) => (
				<FutureParkerEditor
					parker={parker}
					loading={loading || sequenceNumberUpdating}
					updateSequenceNumber={handleSequenceNumberUpdate}
				/>
			),
		},
		{
			field: 'name',
			headerName: 'Parker Name',
			editable: false,
			groupable: false,
			flex: 1,
		},
		{
			field: 'accountId',
			headerName: 'Account ID',
			editable: false,
			groupable: false,
			flex: 1,
			filterOperators: numericOperators,
			valueGetter: adaptGridValueCallback(({ row }) => row.accountId),
			valueFormatter: adaptGridValueCallback(({ value }) => `AC-${value}`),
		},
		{
			field: 'rate',
			headerName: 'Rate',
			editable: false,
			groupable: false,
			flex: 1,
		},
		{
			field: 'sublot',
			headerName: 'Sublot',
			editable: false,
			groupable: false,
			flex: 1,
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			editable: false,
			groupable: false,
			flex: 1,
			filterOperators: dateOperators,
			renderCell: (params) => dayjs(params.value).utc(false).format('M/D/YYYY'),
		},
	];

	const toolbar = () => (
		<GridToolbarContainer>
			<GridToolbarFilterButton />
			<GridToolbarColumnsButton />
			<GridToolbarDensitySelector />
		</GridToolbarContainer>
	);

	return (
		<DataGridPremium
			rows={parkers}
			columns={columns}
			disableRowSelectionOnClick
			slots={{
				toolbar,
				loadingOverlay: DatagridLinearProgress,
			}}
			slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
			loading={loading || sequenceNumberUpdating}
			pagination
			paginationMode='client'
			pageSizeOptions={[pageSize]}
			paginationModel={{ page, pageSize }}
			onPaginationModelChange={setPaginationModel}
			sortingMode='client'
			filterModel={filterModel}
			onFilterModelChange={(newFilterModel) => {
				for (const item of newFilterModel.items) {
					if (['accountId', 'sequenceNumber'].includes(item.field)) {
						item.value = Number(item.value);
					}
				}

				setFilterModel(newFilterModel);
			}}
		/>
	);
};
